// Simple vanilla accordion (no jQuery)
// Uses explicit height transitions (not max-height). Targets elements with
// `.simpordion` (the header links) and their href/data-target to find the
// content element. Looks for `.simpordion-content` as the
// inner wrapper to calculate height. Also supports finding a sibling `.simpordion-content`
// if no href/data-target is provided. Ensures the element is displayed before measuring.

export function initNativeAccordion(root = document) {
    const toggles = Array.from(root.querySelectorAll('.simpordion'));

    toggles.forEach(toggle => {
        // Try href/data-target first, then fall back to sibling/parent lookup
        const targetSelector = toggle.getAttribute('href') || toggle.getAttribute('data-target');
        let target = null;

        if (targetSelector) {
            target = document.querySelector(targetSelector);
        }

        if (!target) {
            // fallback: next sibling or parent li's .simpordion-content
            const next = toggle.nextElementSibling;
            if (next && next.classList && next.classList.contains('simpordion-content')) {
                target = next;
            } else {
                const parentLi = toggle.closest('li');
                if (parentLi) {
                    target = parentLi.querySelector('.simpordion-content') || parentLi.querySelector('.accordion-content');
                }
            }
        }

        if (!target) return;

        // Find the inner wrapper used for measuring content height.
        const wrapper = target.querySelector('.simpordion-content, .accordion-content-wrapper, .answer-wrapper') || target;

        // Setup initial styles
        target.style.overflow = 'hidden';
        target.style.transition = 'height 260ms ease';

        // Initialize closed or open based on header's 'collapsed' class
        if (toggle.classList.contains('collapsed')) {
            target.style.height = '0px';
            target.classList.remove('is-open');
            toggle.setAttribute('aria-expanded', 'false');
            // If the CSS has a .collapse rule that hides element (display:none), keep it.
        } else {
            // If open by default, ensure it's visible and allow auto height
            target.style.display = target.style.display || '';
            target.style.height = 'auto';
            target.classList.add('is-open');
            toggle.setAttribute('aria-expanded', 'true');
        }

        // Click handler
        toggle.addEventListener('click', (ev) => {
            ev.preventDefault();
            const isOpen = !toggle.classList.contains('collapsed');
            if (isOpen) {
                collapseElement(target, toggle);
            } else {
                // If the toggle is inside a list and not marked as multi, close siblings
                const parentUL = toggle.closest('ul');
                const multi = toggle.getAttribute('data-multi');
                if (parentUL && multi !== 'true') {
                    const otherToggles = Array.from(parentUL.querySelectorAll('.simpordion'));
                    otherToggles.forEach(ot => {
                        if (ot === toggle) return;
                        const sel = ot.getAttribute('href') || ot.getAttribute('data-target');
                        let othertarget = null;
                        if (sel) othertarget = document.querySelector(sel);
                        if (!othertarget) {
                            const next = ot.nextElementSibling;
                            if (next && next.classList && next.classList.contains('simpordion-content')) othertarget = next;
                            else {
                                const parentLi2 = ot.closest('li');
                                if (parentLi2) othertarget = parentLi2.querySelector('.simpordion-content');
                            }
                        }
                        if (othertarget) collapseElement(othertarget, ot);
                    });
                }
                expandElement(target, toggle, wrapper);
            }
        });

        // Close on transitionend if height reaches 0 to keep DOM tidy
        target.addEventListener('transitionend', (e) => {
            // Only act on the height transition
            if (e.propertyName !== 'height') return;

            if (target.style.height === '0px') {
                target.classList.remove('is-open');
                // restore display to allow CSS to hide if needed
                target.style.display = '';
            } else {
                // when open, set height auto to allow responsive content
                if (target.classList.contains('is-open')) {
                    target.style.height = 'auto';
                }
            }
        });
    });

    function expandElement(target, toggle, wrapper) {
        // Ensure the element is visible so scrollHeight is measurable.
        // Use block as a safe fallback; if you need different display types, add a data-display attribute.
        target.style.display = 'block';

        // Measure
        const height = wrapper.scrollHeight;
        // Ensure current height is 0 or pixel value
        target.style.height = '0px';
        // Force reflow so the transition occurs
        target.getBoundingClientRect();
        target.classList.add('is-open');
        toggle.classList.remove('collapsed');
        toggle.setAttribute('aria-expanded', 'true');
        // Animate to measured height
        target.style.height = height + 'px';
    }

    function collapseElement(target, toggle) {
        const wrapper = target.querySelector('.simpordion-content, .accordion-content-wrapper, .answer-wrapper') || target;
        // If currently auto, measure current height
        // If height is auto, wrapper.scrollHeight gives the full height
        const height = wrapper.scrollHeight;
        // Set explicit height to allow transition
        target.style.height = height + 'px';
        // Force reflow
        target.getBoundingClientRect();
        // Animate to 0
        requestAnimationFrame(() => {
            target.style.height = '0px';
        });
        toggle.classList.add('collapsed');
        toggle.setAttribute('aria-expanded', 'false');
    }
}

// Auto-init on load
function setup() {
    initNativeAccordion(document);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
} else {
    setup();
}
